import {
  Box,
  Button,
  Typography,
  useTheme,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from 'react-i18next';
import { PaymentMethodStatus, PayoutDialogContentType } from '../../../common/enums';
import { PaymentMethodDetails } from './Common';
import { ReactComponent as PaypalLogo } from '../../../assets/images/paypal_logo.svg';
import { ReactComponent as BankTransferLogo } from '../../../assets/images/bank_transfer_logo.svg';
import { ReactComponent as ChequeLogo } from '../../../assets/images/cheque.svg';
import { TestIds } from '../../../assets/testIds';
import { PaymentMethodType } from '../../../common/models/PaymentMethod';

type PaymentMethodCardProps = {
  type: string,
  onManageClick?: () => void;
  setDialogContentType?: (event: PayoutDialogContentType) => void;
  setDialogSubTitle?: (event: string | JSX.Element) => void;
  setPaymentMethodId?: (event: string) => void;
  disableConnect?: boolean;
};

export function PaymentMethodCard({
  type,
  onManageClick,
  setDialogContentType,
  setDialogSubTitle,
  setPaymentMethodId,
  disableConnect = false,
}: PaymentMethodCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const paymentMethodDetails = PaymentMethodDetails();
  const methodDetails =
    paymentMethodDetails[type as keyof typeof paymentMethodDetails];

  const handleSetAsDefault = (paymentMethodType: string) => {
    setDialogContentType?.(PayoutDialogContentType.SET_ACTIVE_ACCOUNT);
    const methodDetailsAsDefault =
      paymentMethodDetails[paymentMethodType as keyof typeof paymentMethodDetails];
    const newSubTitle = (
      <>
        {t('wallet.payoutSettingsDialog.subTitle.changeActive')}
        <Typography
          variant='body4'
          component='span'
          sx={{
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.spacing(2),
            },
          }}
        >
          {`${methodDetailsAsDefault?.name} (${methodDetailsAsDefault?.account})?`}
        </Typography>
      </>
    );
    setPaymentMethodId?.(methodDetailsAsDefault?.id || '');
    setDialogSubTitle?.(newSubTitle);
  };

  const getLogoByType = (logoType: string) => {
    switch (logoType) {
      case PaymentMethodType.PAYPAL:
        return <PaypalLogo height='auto' width='auto' />;
      case PaymentMethodType.BANK_ACCOUNT:
        return <BankTransferLogo />;
      case PaymentMethodType.PAPER_CHEQUE:
        return <ChequeLogo />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        flex: '1 0 0',
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
        border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
        alignSelf: 'stretch',
        minWidth: theme.breakpoints.values.sm,
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1),
          minWidth: 'auto',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(2),
          flex: '1 0 0',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            padding: `${theme.spacing(1.125)} ${theme.spacing(2.625)} ${theme.spacing(1)} ${theme.spacing(2.4)}`,
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: theme.spacing(0.75),
            border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
            height: theme.spacing(6.25),
            width: theme.spacing(8.75),
            [theme.breakpoints.down('sm')]: {
              width: theme.spacing(7.5),
            },
          }}
        >
          {getLogoByType(methodDetails?.type)}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: theme.spacing(1),
            alignSelf: 'stretch',
            flex: '1 0 0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignSelf: 'stretch',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 0 0',
                alignSelf: 'center',
                [theme.breakpoints.down('sm')]: {
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'visible',
                  flexWrap: 'wrap',
                },
              }}
            >
              <Typography variant='h6'>
                {methodDetails?.name}
              </Typography>
              {
                methodDetails?.paymentMethodExist &&
                methodDetails?.status !== PaymentMethodStatus.UNVERIFIED &&
                < Typography variant='body8'
                  sx={{
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word',
                    marginRight: theme.spacing(0.8),
                  }}
                >
                  {methodDetails?.account}
                </Typography>
              }
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: theme.spacing(1.5),
                [theme.breakpoints.down('sm')]: {
                  flexDirection: 'column',
                },
              }}
            >
              {
                methodDetails?.paymentMethodExist &&
                !methodDetails?.isActive &&
                methodDetails?.status !== PaymentMethodStatus.UNVERIFIED &&
                <Button
                  onClick={() => handleSetAsDefault(type)}
                  sx={{
                    color: theme.palette.common.black,
                    '&:hover': {
                      cursor: 'pointer',
                      backgroundColor: 'transparent',
                    },
                    [theme.breakpoints.down('sm')]: {
                      padding: `${theme.spacing(0.75)} ${theme.spacing(1.25)}`,
                      minWidth: 'unset',
                    },
                  }}
                >
                  <Typography
                    variant='h6'
                    sx={{
                      [theme.breakpoints.down('sm')]: {
                        fontSize: theme.spacing(1.55),
                      },
                    }}
                  >
                    {t('button.setAsActive')}
                  </Typography>
                </Button>
              }
              {
                methodDetails?.paymentMethodExist &&
                methodDetails?.isActive &&
                methodDetails?.status !== PaymentMethodStatus.UNVERIFIED &&
                <Button
                  startIcon={<CheckIcon />}
                  sx={{
                    padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
                    justifyContent: 'center',
                    gap: theme.spacing(0.5),
                    borderRadius: theme.spacing(12.5),
                    border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
                    boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
                    color: '#A4A7AE',
                    cursor: 'default',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    [theme.breakpoints.down('sm')]: {
                      padding: `${theme.spacing(0.75)} ${theme.spacing(1.25)}`,
                      minWidth: 'unset',
                    },
                  }}
                >
                  <Typography
                    variant='h15'
                    sx={{
                      [theme.breakpoints.down('sm')]: {
                        fontSize: theme.spacing(1.75),
                      },
                    }}
                  >
                    {t('button.active')}
                  </Typography>
                </Button>
              }
              <Button
                data-testid={TestIds.managePayoutSettings.connectBtn}
                onClick={onManageClick}
                disabled={
                  disableConnect &&
                  !(
                    methodDetails?.paymentMethodExist &&
                    methodDetails?.status !== PaymentMethodStatus.UNVERIFIED
                  )
                }
                sx={{
                  padding: `${theme.spacing(1.25)} ${theme.spacing(1.75)}`,
                  color: theme.palette.common.black,
                  borderRadius: theme.spacing(12.5),
                  border: `${theme.spacing(0.125)} solid ${blueGrey[200]}`,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&:disabled': {
                    color: theme.palette.grey[400],
                    borderColor: theme.palette.grey[300],
                    cursor: 'not-allowed',
                  },
                  [theme.breakpoints.down('sm')]: {
                    padding: `${theme.spacing(0.75)} ${theme.spacing(1.25)}`,
                    minWidth: 'unset',
                  },
                }}
              >
                <Typography
                  variant='h6'
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      fontSize: theme.spacing(1.75),
                    },
                  }}
                >
                  {
                    (methodDetails?.paymentMethodExist &&
                    methodDetails?.status !== PaymentMethodStatus.UNVERIFIED) ?
                      t('button.manage') : t('button.connect')
                  }
                </Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {methodDetails?.status === PaymentMethodStatus.INVALID && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(1),
            alignSelf: 'stretch',
            flex: '1 0 0',
          }}
        >
          <ErrorIcon color="error" fontSize="small" />
          <Typography
            variant="body8"
            color="error"
            sx={{
              [theme.breakpoints.down('sm')]: {
                fontSize: theme.spacing(1.75),
                wordBreak: 'break-all',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
              },
            }}
          >
            {t('wallet.payoutSettingsDialog.invalidAccount')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
